import React, { useState, useEffect } from 'react';
import Select from 'react-select/async';
import { OrgService, Organization } from 'app/services/OrgService';
import { JobfairService } from 'app/services/JobfairService';
import { JobfairCard } from 'app/modules/shared/jobfairs/jobfair-card';
import { JobFair } from 'app/types';
import CreateEditJobFair from 'app/modules/shared/jobfairs/CreateEditJobFair';
import { Alert, Button, Modal } from 'react-bootstrap-v5';
import { AlertModal } from '../shared/modals/AlertModal';

export const AdminJobfairs: React.FC = () => {
    const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [jobfairs, setJobfairs] = useState<JobFair[]>([]);
    const [activationFilter, setActivationFilter] = useState<'all' | 'activated' | 'notActivated'>('all');
    const [selectedJobFair, setSelectedJobFair] = useState<JobFair | undefined>();
    const [showModal, setShowModal] = useState(false);
    const [showJobFairUpdatedAlertPanel, setShowJobFairUpdatedAlertPanel] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobfairToDelete, setJobfairToDelete] = useState<JobFair | null>(null);
    const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');

    const orgService = new OrgService();
    const jobFairService = new JobfairService();

    const handleCreateJobFair = async (values: any) => {

        if (selectedJobFair && selectedOrg?.id) {
            await jobFairService.updateJobFair({ ...selectedJobFair, ...values });
            setShowModal(false);
            setShowJobFairUpdatedAlertPanel(true);
            await loadJobfairs(selectedOrg?.id)
        }
    };

    const handleDeleteJobFair = (jf: JobFair) => {
        setJobfairToDelete(jf);
        setDeleteConfirmationInput('');
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (deleteConfirmationInput === 'Delete' && jobfairToDelete && selectedOrg?.id) {
            await jobFairService.deleteJobFair(selectedOrg.id, jobfairToDelete.id);
            setShowDeleteModal(false);
            setJobfairToDelete(null);
            setDeleteConfirmationInput('');
            await loadJobfairs(selectedOrg.id);
        }
    };

    useEffect(() => {
        const loadOrganizations = async () => {
            try {
                const result = await orgService.getAllOrganizations(100); // Add reasonable limit
                setAllOrgs(result.organizations); // Access organizations array from result
            } catch (error) {
                setAllOrgs([]);
            }
        };

        loadOrganizations();
    }, []);

    useEffect(() => {

        if (allOrgs && allOrgs.length === 0) return;

        // read selected org from query params react way
        const urlParams = new URLSearchParams(window.location.search);
        const orgId = urlParams.get('orgId');
        if (orgId) {
            const org = allOrgs.find(org => org.id === orgId);
            if (org) {
                setSelectedOrg(org);
                loadJobfairs(orgId);
            }
        }
    }, [allOrgs]);

    const loadJobfairs = async (orgId: string) => {
        const fairs = await jobFairService.getJobFairs(orgId);
        setJobfairs(fairs);
    };

    const handleOrgChange = (selected: any) => {
        if (selected?.org?.id) {
            setSelectedOrg(selected.org);
            loadJobfairs(selected.org.id);

            // Update the orgId in the URL query string
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('orgId', selected.org.id);
            window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
        }
    };

    const filteredJobfairs = jobfairs.filter(fair => {
        if (activationFilter === 'activated') return fair.isActivated;
        if (activationFilter === 'notActivated') return !fair.isActivated;
        return true;
    });

    const formatOptionLabel = (option: any) => ({
        value: option.id,
        label: option.companyName,
        org: option
    });


    const loadOptions = async (inputValue: string) => {
        const filteredOrgs = allOrgs.filter(org =>
            org.companyName.toLowerCase().includes(inputValue.toLowerCase())
        );
        return filteredOrgs.map(formatOptionLabel);
    };

    return (
        <div className='container mt-10'>
            {allOrgs?.length > 0 && <div className='row mb-5'>
                <div className='col-md-8'>
                    <div style={{ position: 'relative', zIndex: 1050 }}>
                        <Select
                            cacheOptions
                            defaultOptions={allOrgs.map(formatOptionLabel)}
                            options={allOrgs.map(org => ({ value: org, label: org.companyName }))}
                            loadOptions={loadOptions}
                            onChange={handleOrgChange}
                            placeholder="Search organization..."
                            value={selectedOrg ? { value: selectedOrg, label: selectedOrg.companyName } : null}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 1050 }) }}
                        />
                    </div>
                </div>
                <div className='col-md-4'>
                    <select
                        className='form-select'
                        value={activationFilter}
                        onChange={(e) => setActivationFilter(e.target.value as any)}
                    >
                        <option value="all">All Jobfairs</option>
                        <option value="activated">Activated</option>
                        <option value="notActivated">Not Activated</option>
                    </select>
                </div>
            </div>
            }

            {(!filteredJobfairs || filteredJobfairs.length === 0) && <Alert type='warning' className='text-center'>
                <div className='alert-text font-weight-bold'>No Job Fair found for this organization.</div>
            </Alert>}

            <div className='row g-6 g-xl-9'>
                {filteredJobfairs.map(fair => (
                    <JobfairCard
                        key={fair.id}
                        fair={fair}
                        onActivationToggle={() => {
                            if (selectedOrg?.id)
                                loadJobfairs(selectedOrg.id)
                        }}

                        onEditJobFair={(jf) => {
                            setSelectedJobFair(jf);
                            setShowModal(true);
                        }}
                        onDeleteJobFair={handleDeleteJobFair}
                    />
                ))}
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedJobFair ? 'Edit Job Fair' : 'Create New Job Fair'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateEditJobFair orgName={selectedOrg?.companyName || ''} onSubmit={handleCreateJobFair} jobFair={selectedJobFair} ></CreateEditJobFair>
                </Modal.Body>
            </Modal>
            <AlertModal show={showJobFairUpdatedAlertPanel} onOK={() => { setShowJobFairUpdatedAlertPanel(false) }} >
                Job Fair updated successfully.
            </AlertModal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Job Fair</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-5">
                        <div className="text-danger fw-bolder fs-3 mb-2">Are you sure?</div>
                        <div className="text-gray-600 fw-bold">
                            This action cannot be undone. All data associated with <strong>{jobfairToDelete?.name}</strong> will be permanently deleted.
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold text-gray-700 fs-7">Type "Delete" to confirm</label>
                        <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder="Delete"
                            value={deleteConfirmationInput}
                            onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        disabled={deleteConfirmationInput !== 'Delete'}
                        onClick={handleConfirmDelete}
                    >
                        Delete Permanently
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>

    );
};